import * as api from "@/apiv2/dynamic-tables/views";
import * as alertActions from "@/redux/alerts/alerts.actions";
import * as routerSelectors from "@/redux/router/router.selectors";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";

import * as actionTypes from "./dynamic-table-views.actionTypes";
import * as selectors from "./dynamic-table-views.selectors";

export const reset = () => (dispatch) => {
  dispatch({
    type: actionTypes.RESET,
  });
};

export const setViews = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_VIEWS,
    payload: data,
  });
};

export const setActiveView = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_ACTIVE_VIEW,
    payload: data,
  });
};

export const setIsFetchingStatus = (status) => (dispatch) => {
  dispatch({ type: actionTypes.SET_IS_FETCHING_STATUS, payload: status });
};

export const setSelectedView = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_SELECTED_VIEW,
    payload: status,
  });
};

export const setDefaultView = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_DEFAULT_VIEW,
    payload: status,
  });
};

export const setIsVisible = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_VISIBLE,
    payload: status,
  });
};

export const setIsCreateViewFormVisible = (status) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_IS_CREATE_VIEW_FORM_VISIBLE,
    payload: status,
  });
};

export const fetchViews = (params) => async (dispatch) => {
  try {
    dispatch(setIsFetchingStatus(STATUS.PENDING));

    const { data } = await api.getViews(params)();

    dispatch(setViews([{ uid: "none", name: "None" }, ...data]));

    dispatch(setIsFetchingStatus(STATUS.FULFILLED));

    const defaultView = data.find((d) => d.isDefault);

    if (defaultView) {
      dispatch(setSelectedView(defaultView));
      dispatch(setDefaultView(defaultView));
      return;
    }

    dispatch(setSelectedView({ uid: "none", name: "None" }));
    dispatch(setDefaultView({ uid: "none", name: "None" }));
  } catch (error) {
    dispatch(setIsFetchingStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

export const saveDefaultView =
  ({ uid, module }) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const views = selectors.getViews(state);
      const view = views.find((v) => v.uid === uid);

      // check if uid belongs to None option
      if (view && view.name === "None") {
        await api.postDefaultView({ uid: null, module })();
      } else {
        await api.postDefaultView({ uid, module })();
      }

      dispatch(setDefaultView(view));
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const updateView =
  ({ uid, payload }) =>
  async (dispatch, getState) => {
    const state = getState();
    const activeView = selectors.getActiveView(state);
    const views = selectors.getViews(state);
    const { data } = await api.patchView({
      uid,
      payload,
    })();

    dispatch(
      setViews(
        views.map((v) =>
          v.uid === data.uid
            ? {
                ...v,
                filter: data.filter,
                fields: data.fields,
                sort: data.sort,
                meta: data.meta,
                isDefault: data.isDefault,
              }
            : v
        )
      )
    );

    if (data.isDefault) {
      dispatch(setDefaultView(data));
    }

    return data;
  };

export const createView =
  ({ payload }) =>
  async (dispatch, getState) => {
    const state = getState();
    const views = selectors.getViews(state);

    const { data } = await api.postView(payload)();

    dispatch(setViews([...views, data]));

    return data;
  };

export const saveView =
  ({ payload }) =>
  async (dispatch, getState) => {
    const state = getState();
    const activeView = selectors.getActiveView(state);
    const currentDefaultView = selectors.getDefaultView(state);
    const views = selectors.getViews(state);
    const noneView = views.find((v) => v.name === "None");

    try {
      dispatch(setIsCreateViewFormVisible(false));

      if (payload.name === "None" && noneView) {
          await await dispatch(
            updateView({
              uid: currentDefaultView.uid,
              payload: {
                isDefault: false,
              },
            })
          );

          return noneView;
        }

      const data = activeView
        ? await dispatch(
            updateView({
              uid: activeView.uid,
              payload,
            })
          )
        : await dispatch(createView({ payload }));

      return data;
    } catch (error) {
      return handleError(error, dispatch);
    }
  };

export const removeView = (uid) => async (dispatch, getState) => {
  try {
    const state = getState();
    const module = routerSelectors.getPath(state).substring(1);
    const views = selectors.getViews(state);
    const selectedView = selectors.getSelectedView(state);
    const defaultView = selectors.getDefaultView(state);
    const noneView = views.find((v) => v.name === "None");

    await api.deleteView(uid)();
    dispatch(setViews(views.filter((v) => v.uid !== uid)));
    dispatch(alertActions.createAlertAction(uid, "View Deleted", true, "danger"));
    dispatch(setActiveView(null));
    dispatch(setIsCreateViewFormVisible(false));

    // if Default View is being deleted and
    // also the selected View reset back the default view to `None`
    if (selectedView.uid === uid && defaultView.uid === uid) {
      dispatch(setSelectedView(noneView));
      dispatch(saveDefaultView({ uid: noneView.uid, module }));
    }

    // if the selected view is the same with the view being deleted
    // revert back to the default view
    else if (selectedView.uid === uid && defaultView.uid !== uid) {
      dispatch(setSelectedView(defaultView));
      dispatch(saveDefaultView({ uid: defaultView.uid, module }));
    }
  } catch (error) {
    handleError(error, dispatch);
  }
};
